import {
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  useEffect,
} from 'react';
import { GRAD, type Grad, grad } from '../student/home/ShapesArt';

/*
 * The plain parts every old screen is built of, redrawn in «Форми 2» (chosen 28.10, «распространять по
 * всей системе»): white blocks on the paper without shadows (radius 28), the dark violet pill for the main
 * button and a white one with a hairline for the rest, fields white with a hairline (radius 14), labels as
 * soft rectangles in a gradient, the page title big and light.
 */

const FIELD =
  'rounded-[14px] bg-white text-ink outline-none ring-1 ring-[#E4E0D7] ring-inset transition-shadow placeholder:text-faint focus:ring-2 focus:ring-[var(--color-primary)]';

export function Card({ children }: { children: ReactNode }) {
  return (
    <div className="w-full max-w-md rounded-[28px] bg-white p-8 ring-1 ring-[#E4E0D7] ring-inset">
      {children}
    </div>
  );
}

export function CenteredScreen({ children }: { children: ReactNode }) {
  return <main className="flex min-h-full items-center justify-center p-4">{children}</main>;
}

export function Button({ className = '', ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={`h-11 w-full rounded-full bg-primary px-5 font-medium text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...props}
    />
  );
}

export function Field({
  label,
  id,
  className = '',
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; id: string }) {
  return (
    <label htmlFor={id} className="block">
      <span className="mb-1.5 block text-[13px] font-medium text-ink-soft">{label}</span>
      <input id={id} className={`h-11 w-full px-3.5 ${FIELD} ${className}`} {...props} />
    </label>
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p role="alert" className="text-sm text-danger">
      {children}
    </p>
  );
}

export function PageHeader({ title, actions }: { title: ReactNode; actions?: ReactNode }) {
  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <h1 className="font-display text-[40px] leading-[48px] font-light tracking-[-0.04em] text-ink md:text-[48px] md:leading-[56px]">
        {title}
      </h1>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function Panel({
  title,
  action,
  children,
  className = '',
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-[28px] bg-white p-5 md:p-6 ${className}`}>
      {(title || action) && (
        <div className="mb-4 flex items-center justify-between gap-2">
          {title && (
            <h2 className="font-display text-[24px] leading-8 font-light tracking-[-0.03em] text-ink">
              {title}
            </h2>
          )}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

/** a label's gradient by its meaning */
const badgeTones = {
  neutral: 'stone',
  brand: 'lavender',
  online: 'lavender',
  shelter: 'butter',
  danger: 'coral',
  success: 'mint',
} as const satisfies Record<string, Grad>;

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: keyof typeof badgeTones;
  children: ReactNode;
}) {
  return (
    <span
      className="inline-flex h-6 items-center rounded-[7px] px-2 text-[12px] leading-none font-medium whitespace-nowrap"
      style={{ backgroundImage: grad(badgeTones[tone]), color: GRAD[badgeTones[tone]].ink }}
    >
      {children}
    </span>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 py-6 text-sm text-muted" role="status">
      <span className="size-4 animate-spin rounded-full border-2 border-[#E4E0D7] border-t-primary" />
      {label}
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="py-8 text-center text-[15px] text-muted">{children}</p>;
}

export function Select({
  label,
  className = '',
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { label?: string }) {
  const select = (
    <select className={`h-10 px-3 text-sm ${FIELD} ${className}`} {...props}>
      {children}
    </select>
  );
  if (!label) return select;
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-muted">{label}</span>
      {select}
    </label>
  );
}

export function SecondaryButton({
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={`inline-flex h-10 items-center justify-center gap-1.5 rounded-full bg-white px-4 text-sm font-medium text-ink ring-1 ring-[#E4E0D7] ring-inset transition-colors hover:bg-[#FAF9F6] disabled:opacity-50 ${className}`}
      {...props}
    />
  );
}

/** Choice between a few options, e.g. lesson format. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: ReadonlyArray<{ value: T; label: ReactNode }>;
  onChange: (value: T) => void;
}) {
  return (
    <div className="inline-flex flex-wrap rounded-full bg-[#EBE7DF] p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
          className={`h-8 rounded-full px-3.5 text-sm font-medium transition-colors ${
            o.value === value ? 'bg-white text-ink' : 'text-muted hover:text-ink'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A dialog over the page; Escape or a click outside closes it. */
export function Modal({
  title,
  onClose,
  children,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[rgb(31_26_53/0.28)] p-4 backdrop-blur-[2px] sm:items-center"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[28px] bg-white p-6 text-ink shadow-[0_28px_56px_-30px_rgb(31_26_53/0.5)]"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="font-display text-[26px] leading-8 font-light tracking-[-0.03em]">
            {title}
          </h2>
          <button
            type="button"
            aria-label="×"
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-full text-[20px] leading-none text-primary ring-[1.5px] ring-primary ring-inset transition-colors hover:bg-primary hover:text-white"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
